import type { PropsWithChildren } from 'react';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import { z } from 'zod';

import type { CreatePortCostDefaultInput } from '../../graphql';
import {
  Currency,
  useCreatePortCostDefaultMutation,
  useUpdatePortCostDefaultByIdMutation,
  useCreateChannelCostEstimateClientMutation,
  useUpdateChannelCostEstimateClientMutation,
  PaymentUnit,
  useUpdateSpotMutation,
  VesselArea,
} from '../../graphql';
import type { EventTimeline } from '../core/Timelines';
import {
  Badge,
  Box,
  Button,
  DeleteButton,
  Heading,
  Input,
  InputWithoutArrows,
  Select,
  Table,
  TableCell,
  TableHeader,
  TableHeaders,
  TableRow,
  Text,
} from '../ui';
import { formatCurrency } from '../../lib/format';
import type { CellProps, Column, FooterProps } from 'react-table';
import { useTable } from 'react-table';
import type {
  Cost,
  CostParam,
  CostsByPortCall,
  BunkerGradeParams,
} from './costs/costs';
import { usePortCallCosts } from './costs/costs';
import type { ScenarioSchedule } from '@seaber/common/core/scenario';
import {
  exhaustiveCheck,
  filteredMap,
  isDefined,
  mapKeyBy,
  NONE,
} from '../../lib/util';
import type { Income, IncomeParam } from './income/income';
import { useFreightOverrides, useVoyageIncome } from './income/income';
import { getTimelineTce } from './TCE';
import SimpleReactTable from '../ui/Table/SimpleReactTable';
import type { ChangedEntries } from '../core/Core';
import { initChangedEntries } from '../core/Core';
import type {
  PortCallBunkerOverride,
  PortCallEntry,
} from '@seaber/common/core/entries';
import { isTCOrderEventTimeData } from '../core/TimeData';
import type { ScheduleDataContext } from '../core/ScheduleDataContext';
import type { ClientId } from '../core/ClientId';
import { capitalize, sum } from 'lodash';
import { useCurrentUser, useDefaultCurrency } from '../../state/zustand';
import type { ScenarioLocation, ScenarioOrder } from '../../lib/planningData';
import LoadingSpinner from '../LoadingSpinner';
import { paymentUnitsToOptions } from '../../lib/form-util';
import { paymentUnits } from '../../constants/paymentUnits';
import {
  IntegrationVesonPnlLoadingIndicator,
  useVoyageVesonPnl,
} from '../integrations/VesonPnl';
import type { PnlData } from '../hooks/usePnlData';
import {
  IntegrationVesonRobLoadingIndicator,
  useVoyageVesonBunker,
} from '../integrations/VesonRob';
import type { SelectOption } from '../ui/Select/Select';
import { DefaultMap, mapGroupBy } from '@seaber/common/lib/collections';
import type { Vessel } from '../core/Vessel';
import { useIsDefaultOrgGradeForArea } from '../../lib/org-area-bunker-grade.utils';
import { useChannelCosts } from './costs/channelCost';
import {
  type BunkerConsumptionByPortCall,
  useBunkerCosts,
} from './costs/bunkerCost';
import { useVoyageExtraCosts } from './costs/extraCost';
import {
  type FreightCost,
  type FreightCostParameter,
  useTimelineFreightCosts,
} from './costs/freightCost';
import {
  useGetEuEtsCostsByShipmentId,
  useGetFuelEuCostsByShipmentId,
} from '../pnl/ui/context';
import {
  createCostSubRows,
  SummaryRow,
  type SummaryRowProps,
} from './voyageCosts/SummaryRow';
import { FuelEuBalance, FuelEuCosts } from './voyageCosts/FuelEu';
import { ParamIndicator } from './voyageCosts/ParamIndicator';
import { Co2Emissions, EuEtsCosts } from './voyageCosts/EuEts';

interface CostsProps {
  scenario: ScenarioSchedule;
  /** Only the partial timeline that is relevant to the voyage */
  timeline: EventTimeline;
  focusedOrderIds: Set<number>;
  scheduleContext: ScheduleDataContext | undefined;
  createAndUpdateAssignments?: (changed: ChangedEntries) => void;
  pnlData: PnlData | undefined;
  focusedShipments: Set<ClientId>;
  orders?: ScenarioOrder[];
}

type TCCostRowData = Cost;
type TCIncomeRowData = Income;

const Costs = ({
  scenario,
  timeline,
  focusedOrderIds,
  scheduleContext,
  pnlData,
  focusedShipments,
  createAndUpdateAssignments,
  orders,
}: CostsProps): JSX.Element => {
  if (!pnlData) {
    return <CostsLoadingIndicator />;
  }

  return (
    <Box data-testid="box-voyage-pl-summary" p={4}>
      <Heading mb={4} fontWeight="bold" fontSize={4}>
        Voyage P/L summary
      </Heading>
      {timeline.type === 'tc' ? (
        <TCCostsTable
          timeline={timeline}
          focusedOrderIds={focusedOrderIds}
          scheduleContext={scheduleContext}
          pnlData={pnlData}
          createAndUpdateAssignments={createAndUpdateAssignments}
          isReadOnlyScenario={scenario.isReadOnly}
          focusedShipmentIds={focusedShipments}
          orders={orders}
        />
      ) : timeline.type === 'spot' ? (
        <SpotCostsTable
          scenario={scenario}
          timeline={timeline}
          scheduleContext={scheduleContext}
          pnlData={pnlData}
          focusedShipmentIds={focusedShipments}
        />
      ) : timeline.type === 'coa' ? (
        <CoaCostsTable
          scenario={scenario}
          timeline={timeline}
          scheduleContext={scheduleContext}
          pnlData={pnlData}
          focusedShipmentIds={focusedShipments}
        />
      ) : (
        exhaustiveCheck(timeline)
      )}
    </Box>
  );
};

const CostsLoadingIndicator = () => (
  <Box display="flex" flexDirection="row" p={4}>
    <LoadingSpinner />
    <Text ml={3}>Loading costs</Text>
  </Box>
);

const ChannelCost = ({
  baseCurrency,
  organizationId,
  vesselId,
  vesselTypes,
  costData,
  createAndUpdateAssignments,
}: {
  baseCurrency: Currency;
  organizationId: number | null;
  vesselId: number | null;
  vesselTypes:
    | { id: number; name: string; isVesselClass: boolean }[]
    | undefined;
  costData: Extract<TCCostRowData, { type: 'CHANNEL' }>;
  createAndUpdateAssignments?: (changed: ChangedEntries) => void;
}) => {
  const byVessel = costData.options?.channelCostByChanel?.byVessel;
  const byTypes = costData.options?.channelCostByChanel?.byTypes;
  const byName = costData.options?.channelCostByChanel?.byName;
  const portCallEntry = costData.options?.portCallEntry;
  const channelName = costData.locations[0].name;

  type ChannelCostState = {
    inputValue: number | undefined;
    defaultValue: 'custom' | string | undefined;
    newDefault: 'byName' | 'byVessel' | 'oneTimeOverride' | string | undefined;
    selectedTypeId: number | undefined;
  };

  const [{ inputValue, newDefault, defaultValue, selectedTypeId }, setState] =
    useReducer(
      (state: ChannelCostState, partialState: Partial<ChannelCostState>) => ({
        ...state,
        ...partialState,
      }),
      {
        inputValue: undefined,
        newDefault: undefined,
        defaultValue: undefined,
        selectedTypeId: undefined,
      },
    );

  useEffect(() => {
    const isOneTimeOverrideChannelCost =
      !!costData.options?.oneTimeOverrideChannelCost;
    const selectedId = costData.options?.selectedId;

    setState({
      inputValue: costData.value ?? undefined,
      defaultValue: selectedId ? String(selectedId) : 'custom',
      newDefault: isOneTimeOverrideChannelCost ? 'oneTimeOverride' : 'byName',
    });
  }, [costData]);

  const byNameOption = useMemo(() => {
    if (byName) {
      return [
        {
          value: byName.id,
          label: `Use stored default value for channel ${byName.cost} ${byName.currency}`,
        },
      ];
    }
    return [];
  }, [byName]);

  const byVesselOption = useMemo(() => {
    if (byVessel) {
      return [
        {
          value: byVessel.id,
          label: `Use stored default value for vessel ${byVessel.cost} ${byVessel.currency}`,
        },
      ];
    }
    return [];
  }, [byVessel]);

  const byTypeOptions = useMemo(() => {
    const options = [];
    for (const type of byTypes ?? []) {
      options.push({
        value: type.id,
        label: `Use stored default for vessel attribute ${type.typeName} ${type.cost} ${type.currency}`,
      });
    }

    return options;
  }, [byTypes]);

  const defaultValueOptions = useMemo(
    () => [
      ...byVesselOption,
      ...byTypeOptions,
      ...byNameOption,
      { value: 'custom', label: 'Custom / set new default value' },
    ],
    [byVesselOption, byTypeOptions, byNameOption],
  );

  const existingVesselTypeOptions = useMemo(() => {
    const options = [];
    for (const type of vesselTypes ?? []) {
      options.push({
        value: type.name,
        label: `Store as new default value for type ${type.name}`,
      });
    }
    return options;
  }, [vesselTypes]);

  const newDefaultValueOption = useMemo(
    () => [
      {
        value: 'byName',
        label: 'Store as new default value for channel',
      },
      {
        value: 'byVessel',
        label: 'Store as new default value for vessel',
      },
      ...existingVesselTypeOptions,
      { value: 'oneTimeOverride', label: 'Just a one time override' },
    ],
    [existingVesselTypeOptions],
  );

  const createAndUpdateAssignmentHandler = useCallback(
    (
      oneTimeOverrideChannelCost: number | null,
      channelCostDefaultId: number | null = null,
    ) => {
      if (!portCallEntry) {
        console.warn('PortCallEntry or PortCall data is not available');
        return;
      }
      const changedPortCalls: ChangedEntries['changedPortCalls'] = new Map();

      changedPortCalls.set(portCallEntry.clientId, {
        ...portCallEntry,
        oneTimeOverrideChannelCost,
        channelCostDefaultId,
      });

      const changeMaps: ChangedEntries = initChangedEntries({
        changedPortCalls,
      });

      createAndUpdateAssignments?.(changeMaps);
    },
    [createAndUpdateAssignments, portCallEntry],
  );

  const changeDefaultHandler = useCallback(
    (value?: string) => {
      if (!portCallEntry) return;

      const changedPortCalls: ChangedEntries['changedPortCalls'] = new Map();

      changedPortCalls.set(portCallEntry.clientId, {
        ...portCallEntry,
        oneTimeOverrideChannelCost: null,
        channelCostDefaultId: value ? Number(value) : null,
      });

      const changeMaps: ChangedEntries = initChangedEntries({
        changedPortCalls,
      });

      createAndUpdateAssignments?.(changeMaps);
    },
    [createAndUpdateAssignments, portCallEntry],
  );

  const [createChannelCost] = useCreateChannelCostEstimateClientMutation();
  const createHandler = useCallback(
    async ({
      vesselId,
      vesselTypeId,
    }: {
      vesselId?: number | null;
      vesselTypeId?: number | null;
    }) => {
      try {
        if (!organizationId || !channelName) {
          console.error('Missing required values');
          return;
        }
        const { data } = await createChannelCost({
          variables: {
            input: {
              channelCostEstimate: {
                organizationId,
                channelName,
                channelCost: Number(inputValue),
                channelCostCurrency: baseCurrency,
                ...(vesselId && { vesselId }),
                ...(vesselTypeId && { vesselTypeId }),
              },
            },
          },
        });

        createAndUpdateAssignmentHandler(
          null,
          data?.createChannelCostEstimate?.channelCostEstimate?.id,
        );
      } catch (e) {
        toast.error('Failed to create new channel cost');
        console.error({ e });
      }
    },
    [
      organizationId,
      channelName,
      inputValue,
      baseCurrency,
      createChannelCost,
      createAndUpdateAssignmentHandler,
    ],
  );

  const [updateChannelCost] = useUpdateChannelCostEstimateClientMutation();
  const updateHandler = useCallback(
    async (id: number) => {
      try {
        const { data } = await updateChannelCost({
          variables: {
            input: {
              id,
              channelCostEstimatePatch: {
                channelCost: Number(inputValue),
              },
            },
          },
        });

        createAndUpdateAssignmentHandler(
          null,
          data?.updateChannelCostEstimateById?.channelCostEstimate?.id,
        );
      } catch (e) {
        toast.error('Failed to update channel cost');
        console.error({ e });
      }
    },
    [inputValue, updateChannelCost, createAndUpdateAssignmentHandler],
  );

  const saveHandler = useCallback(async () => {
    try {
      if (newDefault === 'oneTimeOverride') {
        createAndUpdateAssignmentHandler(Number(inputValue));
      } else if (newDefault === 'byName') {
        if (byName) {
          await updateHandler(byName.id);
        } else {
          await createHandler({});
        }
      } else if (newDefault === 'byVessel') {
        if (byVessel) {
          await updateHandler(byVessel.id);
        } else {
          await createHandler({ vesselId });
        }
      } else {
        const id = byTypes?.find((i) => i.typeName === newDefault)?.id;
        if (id) {
          await updateHandler(id);
        } else {
          await createHandler({ vesselTypeId: selectedTypeId });
        }
      }
    } catch (e) {
      toast.error('Failed to save channel cost');
      console.error({ e });
    }
  }, [
    newDefault,
    inputValue,
    byName,
    updateHandler,
    createHandler,
    byVessel,
    vesselId,
    byTypes,
    selectedTypeId,
    createAndUpdateAssignmentHandler,
  ]);

  return (
    <Box display="flex" maxWidth="fit-content" alignItems="center">
      <Select
        name="type"
        mr={3}
        width={400}
        value={defaultValue}
        options={defaultValueOptions}
        enablePlaceholder
        onChange={(e) => {
          const value = e.target.value;
          setState({ defaultValue: value });
          if (value !== 'custom') {
            changeDefaultHandler(value);
          }
        }}
      />
      {defaultValue === 'custom' || defaultValueOptions.length === 1 ? (
        <>
          <Select
            name="type"
            mr={3}
            width={350}
            value={newDefault}
            options={newDefaultValueOption}
            enablePlaceholder
            onChange={(e) => {
              const newDefault = e.target.value;
              const selectedTypeId = vesselTypes?.find(
                (i) => i.name === newDefault,
              )?.id;
              setState({ newDefault, selectedTypeId });
            }}
          />
          <InputWithoutArrows
            type="number"
            width={80}
            value={inputValue}
            onChange={(e) => {
              setState({ inputValue: Number(e.target.value) });
            }}
            mr={3}
            placeholder="Value"
          />
          <Button width="fit-content" variant="secondary" onPress={saveHandler}>
            Save
          </Button>
        </>
      ) : null}
    </Box>
  );
};

const VesselAreaToString = {
  [VesselArea.ALL_AREAS]: 'All Areas',
  [VesselArea.INSIDE_ECA]: 'Inside ECA',
  [VesselArea.OUTSIDE_ECA]: 'Outside ECA',
};

function BunkerCost({
  bunkerGradeParams,
  params,
  vessel,
  portCallEntry,
  createAndUpdateAssignments,
}: {
  bunkerGradeParams?: BunkerGradeParams;
  params: CostParam[];
  vessel: Vessel | null;
  portCallEntry: PortCallEntry | null;
  createAndUpdateAssignments?: (changed: ChangedEntries) => void;
}) {
  const { area, type, tag } = bunkerGradeParams ?? {};
  const { speedConsumptions } = vessel ?? {};
  const existingBunkerOverrides = portCallEntry?.bunkerOverrides;
  const matchingOverride = existingBunkerOverrides?.find(
    (o) => o.area === area && o.type === type && o.tag === tag,
  );

  const isDefaultOrgGradeForArea = useIsDefaultOrgGradeForArea();

  const options = useMemo(() => {
    if (!type) return [];
    const result: (SelectOption & { area: VesselArea })[] = [
      {
        value: '',
        label: '(Default grade)',
        area: VesselArea.ALL_AREAS,
      },
    ];
    const uniqueValues = new Set();

    const scs = [...(speedConsumptions?.get(type)?.values() ?? [])].flat();
    for (const sc of scs) {
      const bunkerGrade = sc.bunkerGradeOverrideByBunkerGradeId;
      if (!bunkerGrade || !sc.area || uniqueValues.has(bunkerGrade.id))
        continue;
      uniqueValues.add(bunkerGrade.id);
      result.push({
        value: bunkerGrade.id,
        area: sc.area,
        label: `${bunkerGrade.gradeName} - (${VesselAreaToString[sc.area]})`,
      });
    }

    return result;
  }, [speedConsumptions, type]);

  const createAndUpdateAssignmentHandler = useCallback(
    (newBunkerGradeId: number | undefined) => {
      if (!portCallEntry || !type || !area) {
        console.warn('PortCallEntry or PortCall data is not available');
        return;
      }

      const newBunkerOverrides = [...(existingBunkerOverrides ?? [])];
      if (newBunkerGradeId === undefined) {
        const index = newBunkerOverrides.findIndex(
          (item) =>
            item.area === area && item.type === type && item.tag === tag,
        );
        if (index !== -1) {
          newBunkerOverrides.splice(index, 1);
        }
      } else {
        const newItem: PortCallBunkerOverride = {
          area,
          type,
          tag: tag ?? null,
          bunker_grade_id: newBunkerGradeId,
        };
        const index = newBunkerOverrides.findIndex(
          (item) =>
            item.area === area &&
            item.type === newItem.type &&
            item.tag === newItem.tag,
        );

        if (index !== -1) {
          // Update existing item
          newBunkerOverrides[index] = newItem;
        } else {
          // Add new item
          newBunkerOverrides.push(newItem);
        }
      }

      const changedPortCalls: ChangedEntries['changedPortCalls'] = new Map();

      changedPortCalls.set(portCallEntry.clientId, {
        ...portCallEntry,
        bunkerOverrides: newBunkerOverrides,
      });

      const changeMaps: ChangedEntries = initChangedEntries({
        changedPortCalls,
      });

      createAndUpdateAssignments?.(changeMaps);
    },
    [
      portCallEntry,
      type,
      area,
      existingBunkerOverrides,
      tag,
      createAndUpdateAssignments,
    ],
  );

  const paramIsOrgDefaultBunkerGrade = (param: CostParam) => {
    if (param.name === 'Bunker grade') {
      const bunkerGrade = param.value as string;
      return isDefaultOrgGradeForArea(area, bunkerGrade);
    }
    return false;
  };

  return (
    <Box display="flex" maxWidth="fit-content" alignItems="center">
      <Select
        options={options}
        value={matchingOverride?.bunker_grade_id ?? ''}
        title="Bunker cost"
        maxWidth="fit-content"
        mr={3}
        enablePlaceholder
        onChange={(e) => {
          const value =
            e.target.value === '' ? undefined : Number(e.target.value);
          createAndUpdateAssignmentHandler(value);
        }}
      />
      <Box display="flex" flexWrap="wrap" justifyContent="flex-start">
        {params.map((param) => {
          const isOrgDefaultBunkerGrade = paramIsOrgDefaultBunkerGrade(param);

          return (
            <ParamIndicator
              key={param.name}
              param={param}
              tooltipText={
                isOrgDefaultBunkerGrade
                  ? 'Organization default for area'
                  : undefined
              }
            />
          );
        })}
      </Box>
    </Box>
  );
}

type PortCallCostState = {
  inputValue: number | undefined;
  defaultValue:
    | 'custom'
    | 'historical'
    | 'oneTimeOverride'
    | 'integration'
    | 'coa'
    | `${'byName' | 'byType' | 'byClass'}/${number}`
    | undefined;
  newDefault:
    | 'oneTimeOverride'
    | `${'byName' | 'byType' | 'byClass'}/${number}/${number}`
    | `byName/NaN/NaN`
    | undefined;
  selectedTypeId: number | undefined;
};

const PortCallCost = ({
  vesselId,
  organizationId,
  baseCurrency,
  costData,
  vesselTypes,
  createAndUpdateAssignments,
  isReadOnlyScenario,
}: {
  vesselId: number | null;
  organizationId: number | null;
  baseCurrency: Currency | null | undefined;
  costData: Extract<TCCostRowData, { type: 'PORT_CALL' }>;
  vesselTypes:
    | { id: number; name: string; isVesselClass: boolean }[]
    | undefined;
  createAndUpdateAssignments?: (changed: ChangedEntries) => void;
  isReadOnlyScenario: boolean;
}) => {
  const integrationCost = costData.options?.integrationCost;
  const portCostPrediction = costData.options?.predictionCost;
  const defaultByName = costData.options?.defaultValue?.byName;
  const defaultByTypes = costData.options?.defaultValue?.byTypes;
  const defaultByClass = costData.options?.defaultValue?.byClass;
  const portCallEntry = costData.options?.portCallEntry;
  const locationId = costData.locations.at(0)?.id;
  const selectedId = costData.options?.selectedId;
  const oneTimeOverrideCost = costData.options?.oneTimeOverrideCost;
  const useHistoricalPortCost = costData.options?.useHistoricalPortCost;
  const useOneTimeOverrideCost = costData.options?.useOneTimeOverrideCost;
  const coaPortCost = costData.options?.coaPortCost;
  const portCostPrefered = costData.options?.selected;
  const isOneTimeOverrideCost = !!oneTimeOverrideCost || useOneTimeOverrideCost;
  const usePortCostDefault = !!selectedId && portCostPrefered === 'default';
  const useIntegrationPortCost = costData.options?.useIntegrationPortCost;

  const initialDefaultValue = useMemo(() => {
    let defaultId: PortCallCostState['defaultValue'] = undefined;
    if (coaPortCost) {
      defaultId = 'coa';
    } else if (useHistoricalPortCost) {
      defaultId = 'historical';
    } else if (useOneTimeOverrideCost) {
      defaultId = 'oneTimeOverride';
    } else if (usePortCostDefault) {
      if (selectedId === defaultByName?.id) {
        defaultId = `byName/${selectedId}`;
      } else if (selectedId === defaultByClass?.id) {
        defaultId = `byClass/${selectedId}`;
      } else if (defaultByTypes?.some((i) => i.id === selectedId)) {
        defaultId = `byType/${selectedId}`;
      }
    } else if (useIntegrationPortCost) {
      defaultId = 'integration';
    } else {
      switch (portCostPrefered) {
        case 'historical': {
          defaultId = 'historical';
          break;
        }
        case 'integration': {
          defaultId = 'integration';
          break;
        }
        case 'override': {
          defaultId = 'oneTimeOverride';
          break;
        }
        default: {
          defaultId = 'custom';
          break;
        }
      }
    }
    return defaultId;
  }, [
    coaPortCost,
    useHistoricalPortCost,
    useOneTimeOverrideCost,
    usePortCostDefault,
    portCostPrefered,
    selectedId,
    defaultByName?.id,
    defaultByClass?.id,
    defaultByTypes,
    useIntegrationPortCost,
  ]);

  const [{ inputValue, defaultValue, newDefault, selectedTypeId }, setState] =
    useReducer(
      (state: PortCallCostState, partialState: Partial<PortCallCostState>) => ({
        ...state,
        ...partialState,
      }),
      {
        inputValue: costData.value ?? 0,
        defaultValue: initialDefaultValue,
        newDefault: isOneTimeOverrideCost
          ? 'oneTimeOverride'
          : `byName/${NaN}/${NaN}`, // NaN is used as a placeholder, for when there is no selected type
        selectedTypeId: undefined,
      },
    );

  const defaultByNameOption = useMemo(() => {
    if (defaultByName) {
      return [
        {
          value: `byName/${defaultByName.id}`,
          label: `Use stored default value for vessel ${defaultByName.cost} ${defaultByName.currency}`,
        },
      ];
    }
    return [];
  }, [defaultByName]);

  const defByTypeOptions = useMemo(() => {
    const options = [];
    for (const type of defaultByTypes ?? []) {
      options.push({
        value: `byType/${type.id}`,
        label: `Use stored default for vessel attribute ${type.typeName} ${type.cost} ${type.currency}`,
      });
    }

    if (defaultByClass) {
      options.push({
        value: `byClass/${defaultByClass.id}`,
        label: `Use stored default for port cost class ${defaultByClass.typeName} ${defaultByClass.cost} ${defaultByClass.currency}`,
      });
    }

    return options;
  }, [defaultByClass, defaultByTypes]);

  const externalValueOption = useMemo(() => {
    const externalValue = costData.options?.historicalValue;
    if (externalValue) {
      return [
        {
          value: 'historical',
          label: `Use historical median value ${externalValue.cost} ${externalValue.currency}`,
        },
      ];
    }

    return [];
  }, [costData]);

  const integrationValueOption = useMemo(() => {
    if (integrationCost) {
      return [
        {
          value: 'integration',
          label: `Use integration value ${integrationCost.value.toFixed(2)} ${
            integrationCost.currency || baseCurrency
          }`,
        },
      ];
    } else if (portCostPrediction) {
      return [
        {
          value: 'integration',
          label: `Use prediction value ${portCostPrediction.value.toFixed(2)}`,
        },
      ];
    }

    return [];
  }, [integrationCost, portCostPrediction, baseCurrency]);

  const coaPortCostValueOption = useMemo(() => {
    if (coaPortCost) {
      return [
        {
          value: 'coa',
          label: `Use COA port cost ${coaPortCost.value.toFixed(2)} ${
            coaPortCost.currency || baseCurrency
          }`,
        },
      ];
    }

    return [];
  }, [coaPortCost, baseCurrency]);

  const existingVesselTypeOptions = useMemo(() => {
    const options = [];
    for (const type of vesselTypes ?? []) {
      const isVesselClass = type.isVesselClass;
      options.push({
        value: `by${isVesselClass ? 'Class' : 'Type'}/${type.id}/${type.name}`,
        label: `Store as new default value for ${
          isVesselClass ? 'class' : 'type'
        } ${type.name}`,
      });
    }
    return options;
  }, [vesselTypes]);

  const newDefaultValueOptions = useMemo(() => {
    return [
      {
        value: 'byName',
        label: 'Store as new default value for vessel',
      },
      ...existingVesselTypeOptions,
      { value: 'oneTimeOverride', label: 'Just a one time override' },
    ];
  }, [existingVesselTypeOptions]);

  const oneTimeOverrideOption = useMemo(() => {
    const oneTimeOverrideCost = costData.options?.oneTimeOverrideCost;
    if (oneTimeOverrideCost) {
      return [
        {
          value: 'oneTimeOverride',
          label: `Use one time override value ${oneTimeOverrideCost} ${baseCurrency}`,
        },
      ];
    }

    return [];
  }, [baseCurrency, costData]);

  const defaultValueOptions = useMemo(
    () => [
      ...defaultByNameOption,
      ...defByTypeOptions,
      ...externalValueOption,
      ...integrationValueOption,
      ...oneTimeOverrideOption,
      ...coaPortCostValueOption,
      { value: 'custom', label: 'Custom / set new default value' },
    ],
    [
      defaultByNameOption,
      defByTypeOptions,
      externalValueOption,
      integrationValueOption,
      oneTimeOverrideOption,
      coaPortCostValueOption,
    ],
  );

  const createAndUpdateAssignmentHandler = useCallback(
    ({
      oneTimeOverrideCost,
      portCostDefaultId,
      useHistoricalPortCost,
      useOneTimeOverrideCost,
      useIntegrationPortCost,
    }: {
      oneTimeOverrideCost: number | null;
      portCostDefaultId: number | null;
      useHistoricalPortCost: boolean;
      useOneTimeOverrideCost: boolean;
      useIntegrationPortCost: boolean;
    }) => {
      if (!portCallEntry) {
        console.warn('PortCallEntry or PortCall data is not available');
        return;
      }
      const changedPortCalls: ChangedEntries['changedPortCalls'] = new Map();

      changedPortCalls.set(portCallEntry.clientId, {
        ...portCallEntry,
        oneTimeOverrideCost,
        portCostDefaultId,
        useHistoricalPortCost,
        useOneTimeOverrideCost,
        useIntegrationPortCost,
      });

      const changeMaps: ChangedEntries = initChangedEntries({
        changedPortCalls,
      });

      createAndUpdateAssignments?.(changeMaps);
    },
    [createAndUpdateAssignments, portCallEntry],
  );

  const [createPortCostDefault] = useCreatePortCostDefaultMutation();
  const createHandler = useCallback(
    async ({
      vesselId,
      vesselTypeId,
    }: {
      vesselId?: number | null;
      vesselTypeId?: number | null;
    }) => {
      try {
        if (!organizationId || !baseCurrency || !locationId) {
          console.error('Missing required values');
          return;
        }
        const createInput: CreatePortCostDefaultInput['portCostDefault'] = {
          organizationId,
          locationId,
          cost: Number(inputValue),
          currency: baseCurrency,
        };

        const { data } = await createPortCostDefault({
          variables: {
            input: {
              portCostDefault: {
                ...createInput,
                vesselId: vesselId ?? null,
                vesselTypeId: vesselTypeId ?? null,
              },
            },
          },
        });

        createAndUpdateAssignmentHandler({
          oneTimeOverrideCost: oneTimeOverrideCost ?? null,
          portCostDefaultId:
            data?.createPortCostDefault?.portCostDefault?.id ?? null,
          useHistoricalPortCost: false,
          useOneTimeOverrideCost: false,
          useIntegrationPortCost: false,
        });

        return data?.createPortCostDefault?.portCostDefault?.id;
      } catch (e) {
        toast.error('Failed to create new port cost default');
        console.error({ e });
      }
    },
    [
      baseCurrency,
      createAndUpdateAssignmentHandler,
      createPortCostDefault,
      inputValue,
      locationId,
      oneTimeOverrideCost,
      organizationId,
    ],
  );

  const [updatePortCostDefault] = useUpdatePortCostDefaultByIdMutation();
  const updateHandler = useCallback(
    async (id: number) => {
      try {
        const { data } = await updatePortCostDefault({
          variables: {
            input: {
              id,
              portCostDefaultPatch: {
                cost: Number(inputValue),
              },
            },
          },
        });

        createAndUpdateAssignmentHandler({
          oneTimeOverrideCost: oneTimeOverrideCost ?? null,
          portCostDefaultId:
            data?.updatePortCostDefaultById?.portCostDefault?.id ?? null,
          useHistoricalPortCost: false,
          useOneTimeOverrideCost: false,
          useIntegrationPortCost: false,
        });
      } catch (e) {
        toast.error('Failed to update port cost default');
        console.error({ e });
      }
    },
    [
      createAndUpdateAssignmentHandler,
      inputValue,
      oneTimeOverrideCost,
      updatePortCostDefault,
    ],
  );

  const saveHandler = useCallback(async () => {
    const typeKey = newDefault?.split('/')[0] as
      | 'oneTimeOverride'
      | 'byName'
      | 'byType'
      | 'byClass';

    try {
      if (typeKey === 'oneTimeOverride') {
        setState({ defaultValue: 'oneTimeOverride' });
        createAndUpdateAssignmentHandler({
          oneTimeOverrideCost: Number(inputValue),
          portCostDefaultId: null,
          useHistoricalPortCost: false,
          useOneTimeOverrideCost: true,
          useIntegrationPortCost: false,
        });
      } else if (typeKey === 'byName') {
        if (defaultByName) {
          await updateHandler(defaultByName.id);
          setState({ defaultValue: `byName/${defaultByName.id}` });
        } else {
          const newId = await createHandler({ vesselId });
          if (newId) {
            setState({ defaultValue: `byName/${newId}` });
          }
        }
      } else if (['byType', 'byClass'].includes(typeKey)) {
        const id = defaultByTypes?.find((i) => i.typeName === newDefault)?.id;

        if (id) {
          await updateHandler(id);
          setState({ defaultValue: `${typeKey}/${id}` });
        } else {
          const newId = await createHandler({
            vesselTypeId: selectedTypeId,
          });
          if (newId) {
            setState({ defaultValue: `${typeKey}/${newId}` });
          }
        }
      }
    } catch (e) {
      toast.error('Failed to save port cost default');
      console.error({ e });
    }
  }, [
    newDefault,
    inputValue,
    defaultByName,
    updateHandler,
    createHandler,
    defaultByTypes,
    vesselId,
    selectedTypeId,
    createAndUpdateAssignmentHandler,
  ]);

  const changeDefaultHandler = useCallback(
    (value: NonNullable<PortCallCostState['defaultValue']>) => {
      if (!portCallEntry) return;

      const [typeKey, typeId] = value.split('/');

      const changedPortCalls: ChangedEntries['changedPortCalls'] = new Map();

      changedPortCalls.set(portCallEntry.clientId, {
        ...portCallEntry,
        useOneTimeOverrideCost: typeKey === 'oneTimeOverride',
        portCostDefaultId:
          typeKey === 'historical' ||
          typeKey === 'oneTimeOverride' ||
          typeKey === 'integration'
            ? null
            : Number(typeId),
        useHistoricalPortCost: typeKey === 'historical',
        useIntegrationPortCost: typeKey === 'integration',
      });

      const changeMaps: ChangedEntries = initChangedEntries({
        changedPortCalls,
      });

      createAndUpdateAssignments?.(changeMaps);
    },
    [createAndUpdateAssignments, portCallEntry],
  );

  const showSaveButton =
    defaultValue === 'custom' || defaultValueOptions.length === 1;

  const historicalTypeAttributeId =
    costData.options?.historicalValue?.typeAttributeId;
  const usingHistoricalByTypeId =
    isDefined(defaultValue) &&
    isDefined(historicalTypeAttributeId) &&
    +defaultValue === historicalTypeAttributeId;
  const showParams =
    defaultValue === 'historical' ||
    defaultValue === 'integration' ||
    usingHistoricalByTypeId;

  const params =
    defaultValue === 'integration'
      ? portCostPrediction?.params || []
      : defaultValue === 'historical'
        ? costData.params
        : [];

  return (
    <Box display="flex" maxWidth="fit-content" alignItems="center">
      <Select
        name="type"
        mr={3}
        width={400}
        value={defaultValue}
        options={defaultValueOptions}
        enablePlaceholder
        onChange={(e) => {
          const value = e.target.value as NonNullable<
            PortCallCostState['defaultValue']
          >;
          setState({ defaultValue: value });
          if (value !== 'custom') {
            changeDefaultHandler(value);
          } else {
            setState({
              newDefault: 'oneTimeOverride',
              inputValue: oneTimeOverrideCost ?? 0,
            });
          }
        }}
        disabled={isReadOnlyScenario}
      />
      {showSaveButton ? (
        <>
          <Select
            name="type"
            mr={3}
            width={350}
            value={newDefault}
            options={newDefaultValueOptions}
            onChange={(e) => {
              const newValue = e.target.value as NonNullable<
                PortCallCostState['newDefault']
              >;

              const [typeKey, typeId, typeName] = newValue.split('/');

              const costMap: { [key: string]: number } = {
                oneTimeOverride: oneTimeOverrideCost ?? 0,
                byName: defaultByName?.cost ?? 0,
                byType:
                  defaultByTypes?.find((i) => i.typeName === typeName)?.cost ??
                  0,
                byClass: defaultByClass?.cost ?? 0,
              };

              const inputValue = costMap[typeKey];

              const selectedTypeId = vesselTypes?.find(
                (i) => i.id === +typeId,
              )?.id;

              setState({ inputValue, selectedTypeId, newDefault: newValue });
            }}
            enablePlaceholder
            disabled={isReadOnlyScenario}
          />
          <InputWithoutArrows
            disabled={isReadOnlyScenario}
            type="number"
            width={80}
            value={inputValue}
            onChange={(e) => {
              setState({ inputValue: Number(e.target.value) });
            }}
            mr={3}
            placeholder="Value"
          />
          <Button
            width="fit-content"
            variant="secondary"
            disabled={isReadOnlyScenario}
            onPress={saveHandler}
          >
            Save
          </Button>
        </>
      ) : showParams ? (
        <Box display="flex" flexWrap="wrap" justifyContent="flex-start">
          {params.map((param) => (
            <ParamIndicator key={param.name} param={param} />
          ))}
        </Box>
      ) : null}
    </Box>
  );
};

const FreightOverrideContext = React.createContext<
  ReturnType<typeof useFreightOverrides> | undefined
>(undefined);

function FreightOverrideProvider(
  props: PropsWithChildren<ReturnType<typeof useFreightOverrides>>,
) {
  const { children, ...rest } = props;

  return (
    <FreightOverrideContext.Provider value={rest}>
      {children}
    </FreightOverrideContext.Provider>
  );
}

function FreightCalculator(props: {
  orderId: number;
  initialFreight: number;
  isReadOnlyScenario: boolean;
}) {
  const context = useContext(FreightOverrideContext);
  if (!context)
    throw new Error(
      'FreightCalculator can only be used inside FreightCalculatorProvider',
    );

  const currentUser = useCurrentUser();
  const configEnableWorldscaleFreights =
    currentUser?.organization?.configEnableWorldscaleFreights ?? false;

  const override = context.freightOverrides[props.orderId];
  const [freightValue, setFreightValue] = useState<string>(
    override?.freight ? `${override.freight}` : `${props.initialFreight}`,
  );

  const [isInvalid, setIsInvalid] = useState(false);

  const filteredPaymentUnits = useMemo(() => {
    if (configEnableWorldscaleFreights) {
      return paymentUnits;
    }
    return paymentUnits.filter((unit) => unit.value !== PaymentUnit.WORLDSCALE);
  }, [configEnableWorldscaleFreights]);

  function onFreightInput(e: React.ChangeEvent<HTMLInputElement>) {
    if (!override) return;
    const value = e.currentTarget.value.replace(',', '.');

    setFreightValue(value);

    const parseResult = z.number().safeParse(+value);

    if (parseResult.success) {
      context?.setFreightOverride(props.orderId, {
        ...override,
        freight: parseResult.data,
      });
      setIsInvalid(false);
    } else {
      setIsInvalid(true);
    }
  }

  function onFreightUnitInput(e: React.ChangeEvent<HTMLSelectElement>) {
    if (!override) return;
    const parseResult = z
      .nativeEnum(PaymentUnit)
      .safeParse(e.currentTarget.value);

    if (parseResult.success) {
      context?.setFreightOverride(props.orderId, {
        ...override,
        freightUnit: parseResult.data,
      });
    }
  }

  return (
    <Box display="inline-flex" mr={2} mb={2}>
      {override ? (
        <Box display="flex" flexDirection="row" alignItems="center">
          <Input
            onChange={onFreightInput}
            value={freightValue}
            width={200}
            borderColor={isInvalid ? 'red' : undefined}
          />
          <Select
            options={paymentUnitsToOptions(filteredPaymentUnits)}
            onChange={onFreightUnitInput}
            value={override.freightUnit}
            width={100}
            mx={2}
          />
          <Button
            variant="secondary"
            mr={2}
            onPress={() => context.persistOverride(props.orderId)}
          >
            Save
          </Button>
          <DeleteButton
            onPress={() => context.toggleFreightOverride(props.orderId, null)}
          />
        </Box>
      ) : (
        !props.isReadOnlyScenario && (
          <Badge
            variant="primary"
            onClick={() =>
              context.toggleFreightOverride(props.orderId, props.initialFreight)
            }
          >
            <Text fontSize={1}>Edit freight +</Text>
          </Badge>
        )
      )}
    </Box>
  );
}

function FreightLumpsumEditable({
  voyageCharterId,
  initialLumpsum,
}: {
  voyageCharterId: number;
  initialLumpsum: number;
}) {
  const [updateSpot] = useUpdateSpotMutation();

  const [lumpsum, setLumpsum] = useState(`${initialLumpsum}`);
  const [changing, setChanging] = useState(false);
  useEffect(() => {
    setLumpsum(`${initialLumpsum}`);
    setChanging(false);
  }, [initialLumpsum]);

  return (
    <Box display="inline-flex" mr={2} mb={2}>
      {changing ? (
        <Box display="flex" flexDirection="row" alignItems="center">
          <Input
            onChange={(e) => setLumpsum(e.target.value)}
            value={lumpsum}
            width={200}
            type="number"
            borderColor={
              !lumpsum || isNaN(parseInt(lumpsum)) || lumpsum === '0'
                ? 'red'
                : undefined
            }
          />

          <Button
            variant="secondary"
            mr={2}
            onPress={() => {
              const newLumpsum = parseInt(lumpsum);
              if (newLumpsum) {
                updateSpot({
                  variables: {
                    input: {
                      id: voyageCharterId,
                      voyageCharterPatch: {
                        lumpsum,
                      },
                    },
                  },
                }).then(() => setChanging(false));
              }
            }}
          >
            Save
          </Button>
          <DeleteButton
            onPress={() => {
              setChanging(false);
              setLumpsum(`${initialLumpsum}`);
            }}
          />
        </Box>
      ) : (
        <Badge variant="primary" onClick={() => setChanging(true)}>
          <Text fontSize={1}>Edit total freight</Text>
        </Badge>
      )}
    </Box>
  );
}

const TCCostsTable = ({
  timeline,
  focusedOrderIds,
  scheduleContext,
  createAndUpdateAssignments,
  pnlData,
  isReadOnlyScenario,
  focusedShipmentIds,
  orders,
}: {
  timeline: EventTimeline;
  focusedOrderIds: Set<number>;
  scheduleContext: ScheduleDataContext | undefined;
  createAndUpdateAssignments?: (changed: ChangedEntries) => void;
  pnlData: PnlData;
  isReadOnlyScenario: boolean;
  focusedShipmentIds: Set<ClientId>;
  orders?: ScenarioOrder[];
}): JSX.Element => {
  const currentUser = useCurrentUser();
  const euEtsCostsEnabled = !!currentUser?.rootOrganization?.euEtsCosts;
  const includeFuelEuInCosts =
    !!currentUser?.rootOrganization?.configIncludeFuelEuInCosts;
  const organizationId = currentUser?.organizationId ?? null;
  const baseCurrency = useDefaultCurrency();

  const { externalShipmentIds, shipmentsByPortCallId } = useMemo(() => {
    const externalShipmentIds = new Set<string>();
    const shipmentsByPortCallId = new DefaultMap<
      ClientId | null,
      Set<ClientId | null>
    >(() => new Set());

    timeline.voyageGroups.forEach((vg) => {
      vg.portCalls.forEach((pc) => {
        pc.tds.forEach((td) => {
          if (!isTCOrderEventTimeData(td)) return;
          const { id: orderId, shipmentId, shipment } = td.event.order;

          shipmentsByPortCallId.get(pc.id).add(shipmentId);
          if (focusedOrderIds.has(orderId) && shipment?.externalShipmentId) {
            externalShipmentIds.add(shipment.externalShipmentId);
          }
        });
      });
    });
    return {
      externalShipmentIds: Array.from(externalShipmentIds),
      shipmentsByPortCallId,
    };
  }, [focusedOrderIds, timeline]);

  const [
    {
      costsLoading,
      bunkerCostsByPortCall,
      portCallCostsByPortCall,
      channelCostsByPortCall,
      tcIncomes,
      errors: costErrors,
    },
    setCosts,
  ] = useState<{
    costsLoading: boolean;
    bunkerCostsByPortCall: CostsByPortCall | null;
    bunkerConsumptionByPortCall: BunkerConsumptionByPortCall | null;
    portCallCostsByPortCall: CostsByPortCall | null;
    channelCostsByPortCall: CostsByPortCall | null;
    tcIncomes: Income[];
    errors: string[];
  }>({
    costsLoading: true,
    bunkerCostsByPortCall: null,
    bunkerConsumptionByPortCall: null,
    portCallCostsByPortCall: null,
    channelCostsByPortCall: null,
    tcIncomes: [],
    errors: [],
  });

  const orderById = useMemo(
    () => mapKeyBy(orders ?? [], (o) => o.id),
    [orders],
  );

  const freightOverrideProps = useFreightOverrides(orderById);
  const {
    vesonBunkerData,
    isEnabled: isVesonRoBEnabled,
    isLoading: isVesonRoBLoading,
  } = useVoyageVesonBunker();
  const { getTimelinePortCallCosts } = usePortCallCosts(pnlData);
  const { getTimelineBunkerCosts } = useBunkerCosts(
    focusedShipmentIds,
    vesonBunkerData,
  );
  const { getTcTimelineIncome } = useVoyageIncome(
    pnlData.incomingVoyageChartersById,
    pnlData.worldscaleFreightRates,
  );
  const { getTimelineChannelCosts } = useChannelCosts(
    pnlData.portCostEstimateMaps,
  );
  const getFuelEuCostsByShipment = useGetFuelEuCostsByShipmentId();
  const getEuEtsCostsByShipment = useGetEuEtsCostsByShipmentId();
  const extraCosts = useVoyageExtraCosts(
    timeline,
    focusedOrderIds,
    pnlData.incomingVoyageChartersById,
    orderById,
  );

  const channels = useMemo(
    () =>
      timeline.voyageGroups
        .flatMap((vg) => vg.portCalls)
        .flatMap((pc) => pc.tds.flatMap((i) => i.usedChannels)),
    [timeline.voyageGroups],
  );

  const vesselTypes = useMemo(() => {
    const { id, name } =
      timeline.vessel?.vesselAttributeByPrimaryAttributeId || {};
    const vesselClass =
      id && name ? { id, name, isVesselClass: true } : undefined;

    const types =
      timeline.vessel?.vesselAttributesAssociationsByShipId?.nodes
        ?.map((n) => {
          const name = n?.vesselAttributeByVesselAttributeId?.name;
          const id = n?.vesselAttributeByVesselAttributeId?.id;
          if (!id || !name) return;
          return { id, name, isVesselClass: false };
        })
        .filter(isDefined) ?? [];

    return vesselClass ? [vesselClass, ...types] : types;
  }, [timeline.vessel]);

  useEffect(() => {
    const fetchAllCosts = async () => {
      try {
        await Promise.all([
          getTimelinePortCallCosts(
            timeline,
            shipmentsByPortCallId,
            focusedShipmentIds,
          ),
          getTimelineBunkerCosts(timeline, focusedOrderIds),
          getTimelineChannelCosts(timeline, channels),
          getTcTimelineIncome(
            timeline,
            focusedOrderIds,
            freightOverrideProps.freightOverrides,
          ),
        ]).then(
          ([
            portCallCostsResult,
            bunkerCostsResult,
            channelCostsResult,
            tcIncomes,
          ]) => {
            setCosts({
              costsLoading: false,
              bunkerCostsByPortCall:
                bunkerCostsResult?.bunkerCosts ?? new Map(),
              bunkerConsumptionByPortCall:
                bunkerCostsResult?.bunkerConsumptionByPortCall ?? new Map(),
              portCallCostsByPortCall:
                portCallCostsResult?.portCallCosts ?? new Map(),
              channelCostsByPortCall:
                channelCostsResult?.channelCosts ?? new Map(),
              tcIncomes,
              errors: [...(portCallCostsResult?.errors ?? [])],
            });
          },
        );
      } catch (e) {
        console.error({ e });
      }
    };
    void fetchAllCosts();
  }, [
    channels,
    focusedOrderIds,
    focusedShipmentIds,
    freightOverrideProps.freightOverrides,
    getTcTimelineIncome,
    getTimelineBunkerCosts,
    getTimelineChannelCosts,
    getTimelinePortCallCosts,
    shipmentsByPortCallId,
    timeline,
  ]);

  const costsByPortCallId = useMemo(() => {
    const portCalls = timeline.voyageGroups.flatMap((vg) => vg.portCalls);
    return new Map<ClientId | null, Cost[]>(
      portCalls.map((pc) => {
        const bunkerCosts = bunkerCostsByPortCall?.get(pc.id) ?? [];
        const portCallCosts = portCallCostsByPortCall?.get(pc.id) ?? [];
        const channelCosts = channelCostsByPortCall?.get(pc.id) ?? [];

        return [pc.id, [...channelCosts, ...bunkerCosts, ...portCallCosts]];
      }),
    );
  }, [
    bunkerCostsByPortCall,
    channelCostsByPortCall,
    portCallCostsByPortCall,
    timeline.voyageGroups,
  ]);

  const allCosts = useMemo(() => {
    const costs: Array<Cost> = [...extraCosts];
    const portCalls = timeline.voyageGroups.flatMap((vg) => vg.portCalls);

    portCalls.forEach((pc) => {
      costs.push(...(costsByPortCallId.get(pc.id) ?? []));
    });

    return costs;
  }, [costsByPortCallId, extraCosts, timeline]);

  const allPortCallEntries = filteredMap(
    allCosts,
    (cost) => ('options' in cost && cost.options?.portCallEntry) || NONE,
  );

  const portCallEntriesByPortClientId = useMemo(() => {
    return mapKeyBy(allPortCallEntries, (entry) => entry.clientId);
  }, [allPortCallEntries]);

  const totalEuEtsCosts = useMemo(
    () =>
      sum(
        Array.from(focusedShipmentIds).flatMap((shipmentId) =>
          getEuEtsCostsByShipment(shipmentId).map(
            (euEtsCost) => euEtsCost?.amount ?? 0,
          ),
        ),
      ),
    [focusedShipmentIds, getEuEtsCostsByShipment],
  );

  const totalFuelEuCosts = useMemo(
    () =>
      sum(
        Array.from(focusedShipmentIds).flatMap((shipmentId) =>
          getFuelEuCostsByShipment(shipmentId).map(
            (fuelEuCost) => fuelEuCost?.amount ?? 0,
          ),
        ),
      ),
    [focusedShipmentIds, getFuelEuCostsByShipment],
  );

  const tce = useMemo(() => {
    return getTimelineTce(
      timeline,
      allCosts,
      tcIncomes,
      euEtsCostsEnabled ? totalEuEtsCosts : 0,
      includeFuelEuInCosts ? totalFuelEuCosts : 0,
    );
  }, [
    timeline,
    allCosts,
    tcIncomes,
    euEtsCostsEnabled,
    totalEuEtsCosts,
    includeFuelEuInCosts,
    totalFuelEuCosts,
  ]);

  const vessel = timeline.vessel;
  const tcCostColumns: Array<Column<TCCostRowData>> = useMemo(
    () => [
      {
        id: 'name',
        accessor: (row) => row.name,
        Header: 'Cost',
        columnWidth: 350,
      },
      {
        id: 'locations',
        accessor: (row) => row.locations,
        Header: 'Port(s)',
        Cell: ({ value }: { value: ScenarioLocation[] }) => {
          return (
            <>
              {value
                .map(
                  (loc) =>
                    `${loc.name?.charAt(0).toUpperCase()}${loc.name?.slice(1)}`,
                )
                .join(', ')}
            </>
          );
        },
        columnWidth: 250,
      },
      {
        id: 'value',
        accessor: (
          row: Extract<TCCostRowData, { type: 'BUNKER' | 'INVOICE_FREIGHT' }>,
        ) => row.value,
        Header: 'Value',
        Cell: ({ value }: { value: number | null }) => {
          return (
            <>
              {value !== null ? formatCurrency(value, 0, baseCurrency) : 'N/A'}
            </>
          );
        },
        Footer: (table: FooterProps<Cost>) => {
          const total = React.useMemo(
            () =>
              table.rows.reduce(
                (sum, row) =>
                  row.values.value !== null ? row.values.value + sum : sum,
                0,
              ),
            [table.rows],
          );

          return (
            <Text fontWeight="bold" data-testid="costs-total-costs">
              {formatCurrency(total, 0, baseCurrency)}
            </Text>
          );
        },
        columnWidth: 200,
      },
      {
        id: 'params',
        accessor: (
          row: Extract<Cost, { type: 'BUNKER' | 'INVOICE_FREIGHT' }>,
        ) => row.params,
        Header: 'Parameters',
        Cell: ({
          value,
          row,
        }: {
          value: CostParam[];
          row: { original: Cost };
        }) => {
          return (
            <>
              {row.original.type === 'PORT_CALL' &&
              !row.original.isExternalPrediction ? (
                <PortCallCost
                  costData={row.original}
                  vesselId={timeline.vesselId}
                  organizationId={organizationId}
                  baseCurrency={baseCurrency ?? Currency.EUR}
                  vesselTypes={vesselTypes}
                  createAndUpdateAssignments={createAndUpdateAssignments}
                  isReadOnlyScenario={isReadOnlyScenario}
                />
              ) : row.original.type === 'CHANNEL' ? (
                <ChannelCost
                  costData={row.original}
                  vesselId={timeline.vesselId}
                  organizationId={organizationId}
                  baseCurrency={baseCurrency ?? Currency.EUR}
                  vesselTypes={vesselTypes}
                  createAndUpdateAssignments={createAndUpdateAssignments}
                />
              ) : row.original.type === 'BUNKER' ? (
                <BunkerCost
                  bunkerGradeParams={row.original.bunkerGradeParams}
                  params={value}
                  vessel={vessel}
                  portCallEntry={row.original.portCallEntry}
                  createAndUpdateAssignments={createAndUpdateAssignments}
                />
              ) : (
                value.map((param) => (
                  <ParamIndicator key={param.name} param={param} />
                ))
              )}
              {row.original.numShipments !== 1 ? (
                <ParamIndicator
                  param={{
                    name: 'Split on shipments',
                    value: row.original.numShipments,
                  }}
                />
              ) : null}
            </>
          );
        },
      },
    ],
    [
      baseCurrency,
      organizationId,
      vesselTypes,
      timeline.vesselId,
      createAndUpdateAssignments,
      isReadOnlyScenario,
      vessel,
    ],
  );

  const tcIncomeColumns: Array<Column<TCIncomeRowData>> = useMemo(
    () => [
      {
        id: 'source',
        accessor: (row) => row.name,
        Header: 'Income source',
        columnWidth: 600,
      },
      {
        id: 'value',
        accessor: (row) => row.value,
        Header: 'Value',
        Cell: ({ value }: { value: number | null }) => {
          return (
            <>
              {value !== null ? formatCurrency(value, 0, baseCurrency) : 'N/A'}
            </>
          );
        },
        Footer: (table) => {
          const total = React.useMemo(
            () =>
              table.rows.reduce(
                (sum, row) =>
                  row.values.value !== null ? row.values.value + sum : sum,
                0,
              ),
            [table.rows],
          );

          return (
            <Text fontWeight="bold" data-testid="income-total-income">
              {formatCurrency(total, 0, baseCurrency)}
            </Text>
          );
        },
        columnWidth: 200,
      },
      {
        id: 'params',
        accessor: (row) => row.params,
        Header: 'Parameters',
        Cell: ({ row, value }: CellProps<TCIncomeRowData, IncomeParam[]>) => {
          const initialFreight = +(
            value.find((i) => i.name === 'Freight rate')?.value ?? 0
          );
          return (
            <>
              {row.original.type === 'ORDER_FREIGHT' &&
              row.original.orderId !== null ? (
                <FreightCalculator
                  orderId={row.original.orderId}
                  initialFreight={initialFreight}
                  isReadOnlyScenario={isReadOnlyScenario}
                />
              ) : null}
              {row.original.type === 'VOYAGE_CHARTER_LUMPSUM' &&
              row.original.voyageCharterId !== null &&
              row.original.value ? (
                <FreightLumpsumEditable
                  voyageCharterId={row.original.voyageCharterId}
                  initialLumpsum={row.original.value}
                />
              ) : null}

              {value.map((param) => (
                <ParamIndicator key={param.name} param={param} />
              ))}
            </>
          );
        },
      },
    ],
    [baseCurrency, isReadOnlyScenario],
  );

  const costsTable = useTable<TCCostRowData>({
    columns: tcCostColumns,
    data: allCosts,
  });

  const incomeTable = useTable<TCIncomeRowData>({
    columns: tcIncomeColumns,
    data: tcIncomes,
  });

  const createGroupedFreightIncomeSubRows = (incomes: Income[]) => {
    return [...mapGroupBy(incomes, (income) => income.type).values()].map(
      (typeIncomes) => {
        const firstIncome = typeIncomes[0];
        return {
          name: capitalize(firstIncome.type.toLowerCase().replace(/_/g, ' ')),
          value: sum(typeIncomes.map((i) => i.value)),
          currency: baseCurrency ?? null,
          subRows: createFreightIncomeSubRows(typeIncomes),
        };
      },
    );
  };
  const createFreightIncomeSubRows = (incomes: Income[]) => {
    return [...mapGroupBy(incomes, (income) => income.orderId).values()].map(
      (incomes) => {
        const firstIncome = incomes[0];
        return {
          name: firstIncome.name,
          value: sum(incomes.map((i) => i.value)),
          currency: baseCurrency ?? null,
        };
      },
    );
  };

  const freightIncomes = tcIncomes.filter(
    (income) =>
      income.type === 'ORDER_FREIGHT' ||
      income.type === 'VOYAGE_CHARTER_LUMPSUM',
  );

  const totalFreightIncome = sum(freightIncomes.map((income) => income.value));

  const freightIncomeSubRows =
    createGroupedFreightIncomeSubRows(freightIncomes);

  const extraFreightIncomes = tcIncomes.filter(
    (income) => income.type === 'EXTRA_FREIGHT',
  );

  const extraFreightIncomeSubRows =
    createFreightIncomeSubRows(extraFreightIncomes);

  const totalExtraFreightIncome = sum(
    extraFreightIncomes.map((income) => income.value),
  );

  const commissionIncomes = tcIncomes.filter(
    (income) => income.type === 'COMMISSION',
  );

  const totalCommissionIncome = sum(
    commissionIncomes.map((income) => income.value),
  );

  const commissionIncomeSubRows = createFreightIncomeSubRows(commissionIncomes);

  const totalIncome = sum(tcIncomes.map((income) => income.value));

  const totalExtraCosts = sum(extraCosts.map((cost) => cost.value));

  const portCostSubRows: SummaryRowProps[] = createCostSubRows(
    costsByPortCallId,
    'PORT_CALL',
    baseCurrency,
    portCallEntriesByPortClientId,
  );

  const bunkerCostSubRows: SummaryRowProps[] = createCostSubRows(
    costsByPortCallId,
    'BUNKER',
    baseCurrency,
    portCallEntriesByPortClientId,
  );

  const channelCostSubRows: SummaryRowProps[] = createCostSubRows(
    costsByPortCallId,
    'CHANNEL',
    baseCurrency,
    portCallEntriesByPortClientId,
  );

  const extraCostSubRows: SummaryRowProps[] = extraCosts.map((cost) => ({
    name: cost.name,
    value: cost.value,
    currency: baseCurrency ?? null,
  }));

  const totalBunkerCosts = sum(bunkerCostSubRows.map((row) => row.value));
  const totalPortCosts = sum(portCostSubRows.map((row) => row.value));
  const totalChannelCosts = sum(channelCostSubRows.map((row) => row.value));

  const totalCosts =
    sum(allCosts.map((cost) => cost.value)) +
    (euEtsCostsEnabled ? totalEuEtsCosts : 0) +
    (includeFuelEuInCosts ? totalFuelEuCosts : 0);

  const totalProfit = totalIncome - totalCosts;

  const {
    isEnabled: isVesonPnlEnabled,
    isLoading: isVesonPnlLoading,
    pnl: vesonPnl,
  } = useVoyageVesonPnl(timeline.vessel?.name ?? '', externalShipmentIds);

  return (
    <FreightOverrideProvider {...freightOverrideProps}>
      {costsLoading && <CostsLoadingIndicator />}
      <Box>
        {tce && tcIncomes.length > 0 ? (
          <>
            {costErrors.map((err) => (
              <>
                <Text key={err} fontWeight="bold" color="status.error">
                  {err}
                </Text>
                <br />
              </>
            ))}
            <Box display="flex" justifyContent="space-between">
              <Heading my={4} fontSize={3} fontWeight="bold">
                Summary
              </Heading>
              {isVesonPnlEnabled && (
                <IntegrationVesonPnlLoadingIndicator
                  isLoading={isVesonPnlLoading}
                />
              )}
            </Box>
            <Box
              borderColor="neutral.gray_225"
              borderWidth={1}
              borderStyle="solid"
              mb={4}
            >
              <Table layout="fixed">
                <thead>
                  <TableHeaders isSticky>
                    <TableHeader
                      width={isVesonPnlEnabled ? 400 : 600}
                    ></TableHeader>
                    <TableHeader width={200}>Value</TableHeader>
                    {isVesonPnlEnabled && (
                      <>
                        <TableHeader width={200}>External</TableHeader>
                        <TableHeader width={200}>Difference</TableHeader>
                      </>
                    )}
                    <TableHeader>Parameters</TableHeader>
                  </TableHeaders>
                </thead>
                <tbody>
                  <SummaryRow
                    name="Freight"
                    value={totalFreightIncome}
                    currency={baseCurrency ?? null}
                    externalValue={
                      isVesonPnlEnabled
                        ? vesonPnl.totalFreightIncome
                        : undefined
                    }
                    isHiddenIfNull
                    subRows={freightIncomeSubRows}
                  />
                  <SummaryRow
                    name="Extra freight"
                    value={totalExtraFreightIncome}
                    currency={baseCurrency ?? null}
                    externalValue={
                      isVesonPnlEnabled
                        ? vesonPnl.totalExtraFreightIncome
                        : undefined
                    }
                    isHiddenIfNull
                    subRows={extraFreightIncomeSubRows}
                  />
                  <SummaryRow
                    name="Commissions"
                    value={totalCommissionIncome}
                    currency={baseCurrency ?? null}
                    externalValue={
                      isVesonPnlEnabled ? vesonPnl.totalCommissions : undefined
                    }
                    isHiddenIfNull
                    subRows={commissionIncomeSubRows}
                  />
                  <SummaryRow
                    name="Other income"
                    value={0}
                    currency={baseCurrency ?? null}
                    externalValue={
                      isVesonPnlEnabled ? vesonPnl.totalOtherIncome : undefined
                    }
                    isHiddenIfNull
                  />
                  <SummaryRow
                    name="Total revenue"
                    value={totalIncome}
                    currency={baseCurrency ?? null}
                    externalValue={
                      isVesonPnlEnabled ? vesonPnl.totalIncome : undefined
                    }
                    isBold
                  />
                  <SummaryRow
                    name="Bunker costs"
                    value={totalBunkerCosts}
                    currency={baseCurrency ?? null}
                    externalValue={
                      isVesonPnlEnabled ? vesonPnl.totalBunkerCost : undefined
                    }
                    isHiddenIfNull
                    subRows={bunkerCostSubRows}
                  />
                  <SummaryRow
                    name="Port costs"
                    value={totalPortCosts}
                    currency={baseCurrency ?? null}
                    externalValue={
                      isVesonPnlEnabled ? vesonPnl.totalPortCost : undefined
                    }
                    isHiddenIfNull
                    subRows={portCostSubRows}
                  />
                  <SummaryRow
                    name="Extra costs"
                    value={totalExtraCosts}
                    currency={baseCurrency ?? null}
                    externalValue={
                      isVesonPnlEnabled ? vesonPnl.totalExtraCost : undefined
                    }
                    isHiddenIfNull
                    subRows={extraCostSubRows}
                  />
                  <SummaryRow
                    name="Channel costs"
                    value={totalChannelCosts}
                    currency={baseCurrency ?? null}
                    isHiddenIfNull
                    subRows={channelCostSubRows}
                  />
                  <SummaryRow
                    name="Other costs"
                    value={0}
                    currency={baseCurrency ?? null}
                    externalValue={
                      isVesonPnlEnabled ? vesonPnl.totalOtherCost : undefined
                    }
                    isHiddenIfNull
                  />
                  <EuEtsCosts
                    externalValuesVisible={isVesonPnlEnabled}
                    isVisible={euEtsCostsEnabled}
                    shipmentIds={focusedShipmentIds}
                  />
                  <FuelEuCosts
                    shipmentIds={focusedShipmentIds}
                    isVisible={includeFuelEuInCosts}
                    externalValuesVisible={isVesonPnlEnabled}
                  />
                  <SummaryRow
                    name="Total costs"
                    value={totalCosts}
                    currency={baseCurrency ?? null}
                    externalValue={
                      isVesonPnlEnabled ? vesonPnl.totalCost : undefined
                    }
                    isBold
                  />
                  <SummaryRow
                    name="Voyage profit"
                    value={totalProfit}
                    currency={baseCurrency ?? null}
                    externalValue={
                      isVesonPnlEnabled ? vesonPnl.totalProfit : undefined
                    }
                    isBold
                  />
                  <TableRow>
                    <TableCell>Voyage TCE</TableCell>
                    <TableCell data-testid="TCE Summary Value">
                      {formatCurrency(tce.value, 0, baseCurrency)} / day
                    </TableCell>
                    {isVesonPnlEnabled && (
                      <>
                        <TableCell>
                          {vesonPnl.tce !== null
                            ? formatCurrency(vesonPnl.tce, 0, baseCurrency)
                            : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {vesonPnl.tce !== null
                            ? formatCurrency(
                                vesonPnl.tce - tce.value,
                                0,
                                baseCurrency,
                                { signDisplay: 'exceptZero' },
                              )
                            : 'N/A'}
                        </TableCell>
                      </>
                    )}
                    <TableCell>
                      {tce.params.map((param) => (
                        <ParamIndicator
                          key={param.name}
                          param={{
                            testId: `voyage-tce-${param.name}`,
                            name: param.name,
                            value: param.value,
                          }}
                        />
                      ))}
                    </TableCell>
                  </TableRow>
                  <Co2Emissions
                    timeline={timeline}
                    scheduleContext={scheduleContext}
                    externalValuesVisible={isVesonPnlEnabled}
                    shipmentIds={focusedShipmentIds}
                  />
                  <FuelEuBalance
                    isVisible={true}
                    shipmentIds={focusedShipmentIds}
                    externalValuesVisible={isVesonPnlEnabled}
                  />
                  <FuelEuCosts
                    shipmentIds={focusedShipmentIds}
                    isVisible={!includeFuelEuInCosts}
                    externalValuesVisible={isVesonPnlEnabled}
                  />
                </tbody>
              </Table>
            </Box>
          </>
        ) : null}
        {tcIncomes.length > 0 ? (
          <>
            <Heading mb={4} fontSize={3} fontWeight="bold">
              Income
            </Heading>
            <SimpleReactTable table={incomeTable} layout="fixed" />
          </>
        ) : null}
        {allCosts.length > 0 ? (
          <>
            <Heading my={4} fontSize={3} fontWeight="bold">
              Costs
            </Heading>
            {isVesonRoBEnabled && (
              <IntegrationVesonRobLoadingIndicator
                isLoading={isVesonRoBLoading}
              />
            )}
            <SimpleReactTable table={costsTable} layout="fixed" />
          </>
        ) : null}
      </Box>
    </FreightOverrideProvider>
  );
};

type CoaSpotCostRowData = FreightCost;

function useCoaSpotCostsColumns(
  baseCurrency: Currency,
): Array<Column<CoaSpotCostRowData>> {
  return useMemo(
    () => [
      {
        id: 'source',
        Header: 'Source',
        accessor: (row) => row.source,
        Footer: () => {
          return <Text fontWeight="bold">Total</Text>;
        },
      },
      {
        id: 'value',
        Header: 'Value',
        accessor: (row) => row.value,

        Cell: ({ value }: { value: number }) => (
          <>{formatCurrency(value, 0, baseCurrency)}</>
        ),

        Footer: (table) => {
          const total = React.useMemo(
            () => table.rows.reduce((sum, row) => sum + row.original.value, 0),
            [table.rows],
          );

          return (
            <Text fontWeight="bold">
              {formatCurrency(total, 0, baseCurrency)}
            </Text>
          );
        },
      },

      {
        id: 'parameters',
        Header: 'Parameters',
        accessor: (row) => row.parameters,
        Cell: ({ value }: { value: FreightCostParameter[] }) => {
          return value.map((param) => (
            <ParamIndicator
              key={param.name}
              param={{
                name: param.name,
                value: param.value,
              }}
            />
          ));
        },
      },
    ],
    [baseCurrency],
  );
}

const CoaCostsTable = ({
  scenario,
  timeline,
  scheduleContext,
  pnlData,
  focusedShipmentIds,
}: {
  scenario: ScenarioSchedule;
  timeline: EventTimeline;
  scheduleContext: ScheduleDataContext | undefined;
  pnlData: PnlData;
  focusedShipmentIds: Set<ClientId>;
}) => {
  const baseCurrency = useDefaultCurrency();

  const coaSpotCostsColumns = useCoaSpotCostsColumns(baseCurrency);
  const {
    getIncome,
    incomes: tcIncomes,
    loading: incomeLoading,
    table: incomeTable,
  } = useCoaSpotIncome(baseCurrency, pnlData);

  const { freightCosts, loading, refetch } = useTimelineFreightCosts(timeline);

  useEffect(() => {
    refetch();
  }, [scenario.clientId, refetch]);

  useEffect(() => {
    getIncome(timeline);
  }, [getIncome, timeline, scenario.clientId]);

  const allCosts = useMemo(() => freightCosts ?? [], [freightCosts]);

  const costsTable = useTable<FreightCost>({
    columns: coaSpotCostsColumns,
    data: allCosts,
  });

  const profit = useMemo(() => {
    let incomes = 0;
    let costs = 0;
    for (const income of tcIncomes) {
      incomes += income.value || 0;
    }
    for (const cost of allCosts) {
      costs += cost.value;
    }
    return { incomes, costs, profit: incomes - costs };
  }, [tcIncomes, allCosts]);

  if (incomeLoading || loading) {
    return <CostsLoadingIndicator />;
  }

  return (
    <Box>
      {tcIncomes.length > 0 ? (
        <>
          <Heading mb={4} fontSize={3} fontWeight="bold">
            Income
          </Heading>
          <SimpleReactTable table={incomeTable} layout="fixed" />
        </>
      ) : null}
      <Heading my={4} fontSize={3} fontWeight="bold">
        Costs
      </Heading>
      <SimpleReactTable table={costsTable} />

      <Heading my={4} fontSize={3} fontWeight="bold">
        Summary
      </Heading>
      <Box borderColor="neutral.gray_225" borderWidth={1} borderStyle="solid">
        <Table layout="fixed">
          <thead>
            <TableHeaders isSticky>
              <TableHeader width={600}></TableHeader>
              <TableHeader width={200}>Value</TableHeader>
              <TableHeader>Parameters</TableHeader>
            </TableHeaders>
          </thead>
          <tbody>
            <TableRow>
              <TableCell>Profit</TableCell>
              <TableCell data-testid="coa-profit">
                {formatCurrency(profit.profit, 0, baseCurrency)}
              </TableCell>
              <TableCell>
                <ParamIndicator
                  param={{ name: 'Income', value: profit.incomes }}
                />
                <ParamIndicator
                  param={{ name: 'Costs', value: profit.costs }}
                />
              </TableCell>
            </TableRow>
            <Co2Emissions
              timeline={timeline}
              scheduleContext={scheduleContext}
              shipmentIds={focusedShipmentIds}
            />
          </tbody>
        </Table>
      </Box>
    </Box>
  );
};

function useCoaSpotIncome(baseCurrency: Currency, pnlData: PnlData) {
  const [loading, setLoading] = useState<boolean>(false);
  const [incomes, setIncomes] = useState<Income[]>([]);
  const { getTcTimelineIncome } = useVoyageIncome(
    pnlData.incomingVoyageChartersById,
    pnlData.worldscaleFreightRates,
  );

  const getIncome = useCallback(
    async (timeline: EventTimeline) => {
      setLoading(true);

      const tcIncomes = await getTcTimelineIncome(timeline);
      setLoading(false);

      if (tcIncomes) {
        setIncomes(tcIncomes);
      }
    },
    [getTcTimelineIncome],
  );

  const columns: Array<Column<TCIncomeRowData>> = useMemo(
    () => [
      {
        id: 'source',
        accessor: (row) => row.name,
        Header: 'Income source',
        columnWidth: 600,
      },
      {
        id: 'value',
        accessor: (row) => row.value,
        Header: 'Value',
        Cell: ({ value }: { value: number | null }) => {
          return (
            <>
              {value !== null ? formatCurrency(value, 0, baseCurrency) : 'N/A'}
            </>
          );
        },
        Footer: (table) => {
          const total = React.useMemo(
            () =>
              table.rows.reduce(
                (sum, row) =>
                  row.values.value !== null ? row.values.value + sum : sum,
                0,
              ),
            [table.rows],
          );

          return (
            <Text fontWeight="bold">
              {formatCurrency(total, 0, baseCurrency)}
            </Text>
          );
        },
        columnWidth: 200,
      },
      {
        id: 'params',
        accessor: (row) => row.params,
        Header: 'Parameters',
        Cell: ({ value }: { value: CostParam[] }) => (
          <>
            {value.map((param) => (
              <ParamIndicator key={param.name} param={param} />
            ))}
          </>
        ),
      },
    ],
    [baseCurrency],
  );

  const table = useTable<TCIncomeRowData>({
    columns,
    data: incomes,
  });

  return { columns, getIncome, incomes, loading, table };
}

const SpotCostsTable = ({
  scenario,
  timeline,
  scheduleContext,
  pnlData,
  focusedShipmentIds,
}: {
  scenario: ScenarioSchedule;
  timeline: EventTimeline;
  scheduleContext: ScheduleDataContext | undefined;
  pnlData: PnlData;
  focusedShipmentIds: Set<ClientId>;
}) => {
  const baseCurrency = useDefaultCurrency();
  const coaSpotCostsColumns = useCoaSpotCostsColumns(baseCurrency);
  const {
    getIncome,
    incomes: tcIncomes,
    loading: incomeLoading,
    table: incomeTable,
  } = useCoaSpotIncome(baseCurrency, pnlData);

  const { freightCosts, loading, refetch } = useTimelineFreightCosts(timeline);

  useEffect(() => {
    refetch();
  }, [scenario.clientId, refetch]);

  useEffect(() => {
    getIncome(timeline);
  }, [getIncome, timeline, scenario.clientId]);

  const allCosts = useMemo(() => freightCosts ?? [], [freightCosts]);

  const costsTable = useTable<FreightCost>({
    columns: coaSpotCostsColumns,
    data: allCosts,
  });

  const profit = useMemo(() => {
    let incomes = 0;
    let costs = 0;
    for (const income of tcIncomes) {
      incomes += income.value || 0;
    }
    for (const cost of allCosts) {
      costs += cost.value;
    }
    return { incomes, costs, profit: incomes - costs };
  }, [tcIncomes, allCosts]);

  if (incomeLoading || loading) {
    return <CostsLoadingIndicator />;
  }

  return (
    <Box>
      {tcIncomes.length > 0 ? (
        <>
          <Heading mb={4} fontSize={3} fontWeight="bold">
            Income
          </Heading>
          <SimpleReactTable table={incomeTable} layout="fixed" />
        </>
      ) : null}
      {allCosts.length > 0 ? (
        <>
          <Heading my={4} fontSize={3} fontWeight="bold">
            Costs
          </Heading>
          <SimpleReactTable table={costsTable} />
        </>
      ) : null}
      {allCosts.length && tcIncomes.length > 0 ? (
        <>
          <Heading my={4} fontSize={3} fontWeight="bold">
            Summary
          </Heading>
          <Box
            borderColor="neutral.gray_225"
            borderWidth={1}
            borderStyle="solid"
          >
            <Table layout="fixed">
              <thead>
                <TableHeaders isSticky>
                  <TableHeader width={600}></TableHeader>
                  <TableHeader width={200}>Value</TableHeader>
                  <TableHeader>Parameters</TableHeader>
                </TableHeaders>
              </thead>
              <tbody>
                <TableRow>
                  <TableCell>Profit</TableCell>
                  <TableCell data-testid="profit">
                    {formatCurrency(profit.profit, 0, baseCurrency)}
                  </TableCell>
                  <TableCell>
                    <ParamIndicator
                      param={{ name: 'Income', value: profit.incomes }}
                    />
                    <ParamIndicator
                      param={{ name: 'Costs', value: profit.costs }}
                    />
                  </TableCell>
                </TableRow>
                <Co2Emissions
                  timeline={timeline}
                  scheduleContext={scheduleContext}
                  shipmentIds={focusedShipmentIds}
                />
              </tbody>
            </Table>
          </Box>
        </>
      ) : null}
    </Box>
  );
};

export default Costs;
